/**
 * Work Profile — Lifecycle view. The whole story of one work on a vertical timeline:
 * Proposal → Admin Sanction → Estimate → Technical Sanction → Tender → LOA →
 * Agreement → Securities & Advances → Execution → M-Book → Bills & Payments →
 * Penalties → Completion. Each stage shows its date, reference, amount and key facts,
 * with its records (bidders, bills, M-Book entries …) in a small table underneath.
 *
 * Data: GET /ds-wms/works/:id/lifecycle (server/api/utility/ds-wms/services/
 * work-lifecycle.service.js). The server shapes every stage the same way, so this
 * component renders them all with one block.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { worksApi, type WorkLifecycle, type LifecycleStage, type LifecycleCellType } from '../../services/wmsService';
import ContractorLink from '../ContractorLink';
import { Skeleton } from '@/components/base/Skeleton';

const STAGE_ICON: Record<string, string> = {
  proposal: 'ri-file-list-3-line',
  admin_sanction: 'ri-government-line',
  estimate: 'ri-calculator-line',
  technical_sanction: 'ri-tools-line',
  tender: 'ri-auction-line',
  loa: 'ri-mail-check-line',
  agreement: 'ri-file-shield-2-line',
  securities: 'ri-safe-2-line',
  execution: 'ri-hammer-line',
  measurements: 'ri-ruler-2-line',
  bills: 'ri-bill-line',
  penalties: 'ri-scales-3-line',
  completion: 'ri-flag-2-line',
};

const STATE_META = {
  done: { label: 'Completed', dot: 'bg-emerald-500 text-white ring-emerald-100 dark:ring-emerald-900/40', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800', line: 'bg-emerald-300 dark:bg-emerald-700' },
  active: { label: 'In progress', dot: 'bg-amber-500 text-white ring-amber-100 dark:ring-amber-900/40', chip: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800', line: 'bg-gray-200 dark:bg-gray-700' },
  pending: { label: 'Not started', dot: 'bg-gray-200 text-gray-400 ring-gray-50 dark:bg-gray-700 dark:text-gray-500 dark:ring-gray-800', chip: 'bg-gray-50 text-gray-500 ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700', line: 'bg-gray-200 dark:bg-gray-700' },
} as const;

/** ₹ in Indian units: Cr from 1 crore, L from 1 lakh, plain rupees below. */
const money = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};
const fullMoney = (v: unknown) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? '' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const date = (v: unknown) => {
  if (!v) return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const humanise = (s: string) => s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const statusTone = (s: string) => {
  const u = s.toUpperCase();
  if (/(PAID|APPROVED|AWARDED|CONVERTED|ISSUED|DONE|CLOSED|RELEASED|FULLY_RECOVERED|COMPLETED|ACTIVE)/.test(u)) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  if (/(REJECT|CANCEL|FORFEIT|DISPUTE|IMPOSED|DEFECT)/.test(u)) return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
  if (/(DRAFT|NOTICE)/.test(u)) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
};
const StatusChip: React.FC<{ value: unknown }> = ({ value }) =>
  value ? <span className={`inline-block rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${statusTone(String(value))}`}>{humanise(String(value))}</span> : <>—</>;

function renderCell(value: unknown, type?: LifecycleCellType, id?: unknown): React.ReactNode {
  switch (type) {
    case 'amount': return <span className="tabular-nums" title={fullMoney(value)}>{money(value)}</span>;
    case 'date': return date(value);
    case 'pct': return value === null || value === undefined || value === '' ? '—' : `${Number(value)}%`;
    case 'bool': return value ? <i className="ri-checkbox-circle-fill text-emerald-500" /> : <span className="text-gray-300">—</span>;
    case 'status': return <StatusChip value={value} />;
    case 'contractor': return <ContractorLink id={id as number} name={value ? String(value) : null} />;
    default: return value === null || value === undefined || value === '' ? '—' : String(value);
  }
}

const StageBlock: React.FC<{ s: LifecycleStage; isLast: boolean; open: boolean; onToggle: () => void }> = ({ s, isLast, open, onToggle }) => {
  const meta = STATE_META[s.state];
  const hasBody = s.facts.length > 0 || !!(s.table && s.table.rows.length);
  return (
    <li className="relative pl-12 pb-5">
      {!isLast && <span className={`absolute left-[19px] top-10 bottom-0 w-[2px] ${meta.line}`} />}
      <span className={`absolute left-0 top-0 inline-flex h-10 w-10 items-center justify-center rounded-full ring-4 ${meta.dot}`}>
        <i className={`${STAGE_ICON[s.key] || 'ri-checkbox-blank-circle-line'} text-lg`} />
      </span>

      <div className={`rounded-xl border ${s.state === 'pending' ? 'border-dashed border-gray-200 dark:border-gray-700' : 'border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800'}`}>
        <button type="button" onClick={hasBody ? onToggle : undefined}
          className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left ${hasBody ? 'cursor-pointer' : 'cursor-default'}`}>
          <span className={`text-[13.5px] font-bold ${s.state === 'pending' ? 'text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-100'}`}>{s.title}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ${meta.chip}`}>{meta.label}</span>
          {s.state === 'pending' ? (
            <span className="text-[12px] text-gray-400 dark:text-gray-500">{s.emptyText}</span>
          ) : (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-gray-500 dark:text-gray-400">
              {s.date && <span><i className="ri-calendar-line mr-1" />{date(s.date)}</span>}
              {s.ref && <span className="font-medium text-gray-700 dark:text-gray-300"><i className="ri-hashtag mr-0.5" />{s.ref}</span>}
            </span>
          )}
          <span className="ml-auto flex items-center gap-3">
            {s.amount !== null && s.amount !== undefined && s.state !== 'pending' && (
              <span className="text-[14px] font-bold tabular-nums text-indigo-700 dark:text-indigo-300" title={fullMoney(s.amount)}>{money(s.amount)}</span>
            )}
            {hasBody && <i className={`ri-arrow-down-s-line text-lg text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />}
          </span>
        </button>

        {hasBody && open && (
          <div className="space-y-3 border-t border-gray-100 px-4 py-3 dark:border-gray-700">
            {s.facts.length > 0 && (
              <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                {s.facts.map((f) => (
                  <div key={f.label} className="min-w-0">
                    <dt className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-400">{f.label}</dt>
                    <dd className="break-words text-[12.5px] font-medium text-gray-800 dark:text-gray-200">{renderCell(f.value, f.type, f.id)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {s.table && s.table.rows.length > 0 && (
              <div>
                {s.table.title && <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-gray-500">{s.table.title}</div>}
                <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
                  <table data-wms-table className="w-full text-[12px]">
                    <thead>
                      <tr>
                        {s.table.columns.map((c) => (
                          <th key={c.key} className={`whitespace-nowrap px-2.5 py-1.5 ${c.type === 'amount' ? 'text-right' : 'text-left'}`}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {s.table.rows.map((r, i) => (
                        <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                          {s.table!.columns.map((c) => (
                            <td key={c.key} className={`px-2.5 py-1.5 text-gray-700 dark:text-gray-300 ${c.type === 'amount' ? 'whitespace-nowrap text-right' : ''}`}>
                              {renderCell(r[c.key], c.type, c.idKey ? r[c.idKey] : undefined)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

const WorkLifecycleTab: React.FC<{ workId?: number | string }> = ({ workId }) => {
  const [data, setData] = useState<WorkLifecycle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!workId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    worksApi.getLifecycle(workId)
      .then((d) => {
        if (!alive) return;
        setData(d);
        // Open every stage that has something to show; pending ones have no body.
        setOpenKeys(new Set(d.stages.filter((s) => s.state !== 'pending').map((s) => s.key)));
      })
      .catch((e) => { if (alive) setError(e?.response?.data?.message || 'Could not load the work lifecycle'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [workId]);

  const counts = useMemo(() => {
    const st = data?.stages ?? [];
    return { done: st.filter((s) => s.state === 'done').length, active: st.filter((s) => s.state === 'active').length, total: st.length };
  }, [data]);

  if (loading) return <div className="space-y-3 p-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>;
  if (error) return <div className="flex flex-col items-center gap-2 py-16 text-sm text-rose-500"><i className="ri-error-warning-line text-3xl" />{error}</div>;
  if (!data) return null;

  const t = data.totals;
  const tiles: Array<[string, number | null, string]> = [
    ['Sanctioned', t.sanctioned, 'text-slate-800 dark:text-slate-100'],
    ['Contract Value', t.contract, 'text-indigo-700 dark:text-indigo-300'],
    ['Measured (M-Book)', t.measured, 'text-teal-700 dark:text-teal-300'],
    ['Billed', t.billed, 'text-sky-700 dark:text-sky-300'],
    ['Paid', t.paid, 'text-emerald-700 dark:text-emerald-300'],
    ['Due (bills in process)', t.due, 'text-amber-700 dark:text-amber-300'],
  ];
  const allOpen = data.stages.filter((s) => s.state !== 'pending').every((s) => openKeys.has(s.key));
  const toggle = (k: string) => setOpenKeys((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  return (
    <div className="mx-auto max-w-6xl space-y-5 px-4 py-5 sm:px-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {tiles.map(([label, v, cls]) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
            <div className={`mt-0.5 text-[17px] font-extrabold tabular-nums ${cls}`} title={fullMoney(v)}>{money(v)}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12.5px] text-gray-600 dark:text-gray-300">
          <span className="font-bold text-emerald-600">{counts.done}</span> of {counts.total} stages completed
          {counts.active > 0 && <> · <span className="font-bold text-amber-600">{counts.active}</span> in progress</>}
        </div>
        <button type="button"
          onClick={() => setOpenKeys(allOpen ? new Set() : new Set(data.stages.filter((s) => s.state !== 'pending').map((s) => s.key)))}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-[11.5px] font-semibold text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
          <i className={allOpen ? 'ri-contract-up-down-line' : 'ri-expand-up-down-line'} />{allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      <ol className="relative">
        {data.stages.map((s, i) => (
          <StageBlock key={s.key} s={s} isLast={i === data.stages.length - 1} open={openKeys.has(s.key)} onToggle={() => toggle(s.key)} />
        ))}
      </ol>
    </div>
  );
};

export default WorkLifecycleTab;
